<template>
    <widget-content v-if="initialized" v-bind="$attrs">
        <transitional :name="transition" :mode="transition === 'fade' ? 'out-in' : null" appear>
            <existing-plans v-if="currentView === -1" :header-placeholder="isSubWidget ? 'none' : 'small'" @new="start" @updated="details" @clicked="login" />

            <start-dca v-if="currentView === 0" :header-placeholder="isSubWidget ? 'none' : 'full'" @completed="next" />

            <authentication v-if="currentView === 1" back-button @back="goTo(startingView)" @completed="next" @signed-in="next" />

            <setup-plan v-if="currentView === 2" @completed="next" @back="back" @verify="verify" />

            <wallet-address-verification v-if="currentView === 4" @back="setup" @completed="verificationCompleted" @failed="setup" />

            <view-plan v-if="currentView === 3" @completed="overview" @back="back" />
        </transitional>
    </widget-content>
</template>

<script lang="ts" setup>
import { useDca } from '@/dollar-cost-average/composables/dca';
import { translationDomain } from '@/dollar-cost-average/types';
import ExistingPlans from '@/dollar-cost-average/views/existing-plans.vue';
import SetupPlan from '@/dollar-cost-average/views/setup-plan.vue';
import StartDca from '@/dollar-cost-average/views/start.vue';
import ViewPlan from '@/dollar-cost-average/views/view-plan.vue';
import getPaymentMethods from '@btcdirect/api/payment-methods';
import { WidgetContent } from '@btcdirect/components';
import { useTranslations, useUserStatus } from '@btcdirect/composables';
import Transitional from '@btcdirect/shared/src/components/transition/transitional.vue';
import { ViewAnimationType } from '@btcdirect/shared/src/components/transition/types';
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { useCurrencyPairs } from '@btcdirect/shared/src/composables/api/system/currency-pairs';
import { useUserAuthentication } from '@btcdirect/shared/src/composables/auth/user-authentication';
import { useAuthenticationStore } from '@btcdirect/stores';
import { defineAsyncComponent, nextTick, onMounted, ref, watch } from 'vue';

const Authentication = defineAsyncComponent(() => import('@btcdirect/embeddable-authentication/src/authentication.vue'));
const WalletAddressVerification = defineAsyncComponent(() => import('@btcdirect/embeddable-wallet-address-verification/src/wallet-address-verification.vue'));

const emit = defineEmits([EmitEvents.BACK, EmitEvents.HIDE, EmitEvents.SHOW]);

const props = defineProps({
    startInOverview: {
        type: Boolean,
        required: false,
        default: false
    },
    isSubWidget: {
        type: Boolean,
        required: false,
        default: false
    }
});

const authenticationStore = useAuthenticationStore();
const { registrationCompleted, status } = useUserStatus();
const { autoAuthenticate } = useUserAuthentication();
const { addTranslationDomain } = useTranslations();
const { retrieveCurrencyPairs } = useCurrencyPairs();
const { dcaRequest, requestCode } = useDca();

const initialized = ref<boolean>(false);

onMounted(async () => {
    getPaymentMethods();

    addTranslationDomain(translationDomain);

    await retrieveCurrencyPairs('f2c');

    if (authenticationStore.user.authenticated) {
        initialized.value = true;
        return;
    }

    await autoAuthenticate();
    initialized.value = true;
});

const startingView: number = !!props.startInOverview ? -1 : 0;

const transition = ref<ViewAnimationType>('fade');
const currentView = ref<number>(startingView);
const previousView = ref<number>(startingView);

watch(
    () => authenticationStore.user.authenticated,
    (received: boolean) => {
        if (!!received || currentView.value === 1) {
            return;
        }

        goTo(startingView, 'fade');
    }
);

const goTo = async (step: number, customTransition?: ViewAnimationType): Promise<void> => {
    emit(step < 1 ? EmitEvents.SHOW : EmitEvents.HIDE);

    transition.value = customTransition ?? (step > currentView.value ? 'slide' : 'slide-reverse');

    await nextTick();
    currentView.value = step;
};

const start = (): void => {
    previousView.value = -1;
    goTo(2);
};

const next = async (): Promise<void> => {
    if (!authenticationStore.user.authenticated) {
        await goTo(1);
        return;
    }

    await status();
    if (!registrationCompleted.value) {
        await goTo(1);
        return;
    }

    if (currentView.value === 0) {
        await goTo(2);
        return;
    }

    if (currentView.value === 1 && previousView.value === -1) {
        await goTo(-1);
        return;
    }

    await goTo(currentView.value + 1);
};

const back = (): void => {
    if ([2, 3].includes(currentView.value) && previousView.value === -1) {
        goTo(-1);
        return;
    }

    if (currentView.value === 2 && authenticationStore.user.authenticated) {
        goTo(0);
        return;
    }

    goTo(currentView.value - 1);
};

const login = (): void => {
    goTo(1);
};

const details = (): void => {
    previousView.value = -1;
    goTo(3);
};

const overview = (): void => {
    goTo(-1);
};

// TODO: Now used for all emits in wallet address verification
const setup = (): void => {
    goTo(2);
};

const verify = (): void => {
    goTo(4);
};

const verificationCompleted = async (): Promise<void> => {
    try {
        await requestCode();
        goTo(3, 'slide');
    } catch (exception) {
        setup();
    }
};
</script>
